import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Package,
  Sparkles,
  Boxes,
  Tag,
  Truck,
  Clock,
  MapPin,
  Layers,
  Recycle,
  Compass,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à domicile ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à domicile
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Notre service de livraison de cartons {city.aCity}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Parce que charger des piles de cartons encombrants dans
              votre véhicule personnel est une contrainte inutile,
              notre service logistique s&apos;occupe de tout et
              s&apos;engage sur trois critères stricts.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "La dépose directe à votre porte",
                body: "Nous acheminons l'intégralité de vos fournitures à plat directement chez vous au Port-Marly, mais aussi dans les villes limitrophes comme Marly-le-Roi, Louveciennes, Le Pecq et Croissy-sur-Seine.",
                Icon: MapPin,
              },
              {
                title: "Une ponctualité rigoureuse",
                body: "Pas de journée perdue à attendre le livreur ; nous fixons un créneau horaire précis adapté à votre emploi du temps pour vous permettre d'attaquer vos cartons immédiatement.",
                Icon: Clock,
              },
              {
                title: "Un matériel livré au sec",
                body: "Toutes vos commandes vous sont remises dans un état de propreté impeccable, hermétiquement protégées face aux intempéries et prêtes à être montées.",
                Icon: ShieldCheck,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Reconditionnés -40% ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Bon plan éco-responsable
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Bon Plan Éco-Responsable : nos cartons reconditionnés à
              moins 40 %
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Une seconde vie pour vos cartons, une économie majeure
              pour votre budget ! Nous collectons, trions et inspectons
              rigoureusement des cartons professionnels qui n&apos;ont
              servi qu&apos;une seule et unique fois. Ces emballages
              reconditionnés conservent 100 % de leur rigidité
              d&apos;origine et de leur double cannelure, mais vous
              sont proposés avec une remise exceptionnelle de 40 % par
              rapport au prix du neuf.
            </p>
            <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              C&apos;est la solution idéale {city.aCity} pour concilier
              transition écologique, zéro déchet et réduction drastique
              de votre budget de déménagement sans faire la moindre
              concession sur la sécurité de vos objets.
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/cartons-demenagement"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
                data-testid="city-le-port-marly-cta-reconditionnes"
              >
                <Recycle className="h-4 w-4" strokeWidth={2.6} />
                Voir nos cartons reconditionnés
                <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
              </Link>
              <Link
                to="/kits-cartons"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
                data-testid="city-le-port-marly-cta-kits"
              >
                <Boxes className="h-4 w-4" strokeWidth={2.6} />
                Découvrir nos kits
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7 space-y-4">
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Recycle className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                  Économie circulaire
                </h3>
              </div>
              <div className="space-y-2.5 text-[14px] text-[#3a4856]">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                  Utilisés une seule fois
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                  Tri &amp; inspection rigoureux
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                  100 % de la rigidité d&apos;origine
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                  Remise exceptionnelle de 40 %
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                  Démarche zéro déchet
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Pourquoi nous choisir ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Nos engagements
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Pourquoi nous choisir pour vos fournitures de déménagement ?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Faire confiance à notre enseigne, c&apos;est s&apos;assurer
            les services d&apos;un partenaire de confiance reconnu dans
            les Yvelines pour son sérieux et la qualité de ses
            produits.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Robustesse éprouvée de qualité industrielle",
              body: "Contrairement aux emballages légers vendus dans les commerces traditionnels qui se déchirent sous le poids, nos cartons sont conçus pour résister à la perforation et à la compression verticale. Vous pouvez les empiler les uns sur les autres dans le camion de transport sans aucun risque d'affaissement ou d'écrasement de vos objets fragiles.",
              Icon: Layers,
            },
            {
              title: "Catalogue ultra-complet centralisé",
              body: "Nous ne vendons pas seulement des cartons. Des rouleaux de film à bulles haute densité aux adhésifs silencieux, en passant par les cartons penderies pour vos vêtements sur cintres ou les croisillons pour vos verres, vous trouvez absolument tout le matériel pro nécessaire pour vider votre logement en un seul clic.",
              Icon: Boxes,
            },
            {
              title: "Ancrage local pour une réactivité à toute épreuve",
              body: "Notre parfaite connaissance de la boucle de la Seine et de ses spécificités géographiques nous permet de déclencher des livraisons rapides et de gérer vos réapprovisionnements de dernière minute. Si vous manquez de cartons en plein milieu de vos préparatifs, notre équipe intervient dans des délais record pour ne jamais bloquer votre élan.",
              Icon: Compass,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-le-port-marly-cta-shop"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir toute la gamme
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/livraison"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-le-port-marly-cta-livraison"
          >
            <Truck className="h-4 w-4" strokeWidth={2.6} />
            Nos modalités de livraison
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-le-port-marly-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
